/**
 * MarketingTourismCity · v2.6.9 batch CF
 * =======================================
 * Per-city Tourism detail page rendered at /tourism/:slug. Pulls the city
 * record out of site_config.tourism.cities by slug and renders:
 *   • Full-width hero image (from the city's hero_image_url)
 *   • City name + tagline
 *   • Rich-text body (rendered as sanitized HTML)
 *   • Attractions list
 *   • Book-a-Ride CTA + Call Dispatch CTA
 *   • Back-to-Tourism link
 *
 * If the slug doesn't match any active city, renders a friendly 404 with
 * a link back to /tourism so the visitor never lands in a dead end.
 */
import { Link, useParams, Navigate } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, ArrowLeft, ArrowRight, Phone, MapPin } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";

export default function MarketingTourismCity() {
  const { slug } = useParams();
  const { cfg, loading } = useSiteConfig();
  if (loading || !cfg) {
    return (
      <MarketingLayout>
        <div className="py-24 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-zinc-500"/></div>
      </MarketingLayout>
    );
  }
  const t = cfg.tourism || { cities: [] };
  const g = cfg.global || {};
  const city = (t.cities || []).find((c) => c.slug === slug && c.enabled !== false);
  if (!city) return <Navigate to="/tourism" replace/>;

  const hasHero = !!city.hero_image_url;
  const body = (city.body && city.body.trim()) || (city.intro || "");
  // v2.7.0 batch CT · Task 4 — per-city SEO meta. Builds a description that
  // naturally weaves the regional keywords for ranking.
  const seoTitle = `Taxi ${city.name}`;
  const seoDescription = `Taxi service to and from ${city.name}. Buller Taxi Services, LLC dispatches 24/7 from Weatherford, Oklahoma — call (901) 459-9448 for a Taxi ${city.name} ride or book online.`;
  return (
    <MarketingLayout>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        canonical={`/tourism/${city.slug}`}
        image={city.hero_image_url || undefined}
        type="article"
        hreflangs={[
          { lang: "en-US", href: `https://bullertaxi.com/tourism/${city.slug}` },
          { lang: "en", href: `https://bullertaxi.com/tourism/${city.slug}` },
          { lang: "x-default", href: `https://bullertaxi.com/tourism/${city.slug}` },
        ]}
      />
      {/* Full-bleed hero */}
      <section
        className="relative border-b border-amber-400/60 overflow-hidden"
        style={
          hasHero
            ? {
                backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.75)), url("${city.hero_image_url}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : { backgroundColor: "#0a0a0a" }
        }
        data-testid={`tourism-city-page-${slug}`}
      >
        <div className="max-w-5xl mx-auto px-5 py-20 sm:py-28 text-center">
          <Link to="/tourism" className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200 text-[11px] uppercase tracking-[0.3em] font-bold mb-3" data-testid="tourism-city-back">
            <ArrowLeft className="h-3 w-3"/>BACK TO TOURISM
          </Link>
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">{city.tagline || "Partner City"}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-zinc-100 mt-2" data-testid="tourism-city-name">{city.name}</h1>
        </div>
      </section>

      {/* Body content */}
      <section className="max-w-4xl mx-auto px-5 py-12 space-y-6" data-testid="tourism-city-body">
        {body && <CityBody html={body}/>}

        {Array.isArray(city.attractions) && city.attractions.length > 0 && (
          <Card className="bg-zinc-900 border-amber-400/30 p-6 mt-6" data-testid="tourism-city-attractions">
            <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold mb-3">THINGS TO DO IN {city.name.toUpperCase()}</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {city.attractions.map((a, i) => (
                <li key={`${a}-${i}`} className="flex items-start gap-2 text-sm text-zinc-200">
                  <span className="inline-block w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0"/>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <div className="flex flex-wrap gap-3 pt-3">
          <Link to={`/ride/login?destination=${encodeURIComponent(city.name)}`} data-testid="tourism-city-book">
            <Button className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-wider">
              BOOK A RIDE TO {city.name.toUpperCase()} <ArrowRight className="h-4 w-4 ml-1.5"/>
            </Button>
          </Link>
          {g.phone && (
            <a href={`tel:${g.phone.replace(/\D/g, "")}`} data-testid="tourism-city-call">
              <Button variant="outline" className="bg-transparent border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-zinc-950 font-extrabold uppercase tracking-wider">
                <Phone className="h-4 w-4 mr-1.5"/>CALL DISPATCH
              </Button>
            </a>
          )}
        </div>
      </section>

      {/* Bottom CTA strip · v2.6.9 batch CH · gated by global CMS toggle */}
      {cfg?.global?.show_ready_to_ride_cta && (
      <section className="bg-amber-400 text-zinc-950 py-8 mt-6" data-testid="tourism-city-cta-strip">
        <div className="max-w-7xl mx-auto px-5 flex items-center justify-between gap-5 flex-wrap">
          <div className="flex items-center gap-3">
            <MapPin className="h-6 w-6"/>
            <h2 className="text-xl sm:text-2xl font-black uppercase">More partner cities</h2>
          </div>
          <Link to="/tourism">
            <Button className="bg-zinc-950 text-amber-300 hover:bg-zinc-900 font-extrabold uppercase tracking-wider">
              EXPLORE ALL CITIES <ArrowRight className="h-4 w-4 ml-1"/>
            </Button>
          </Link>
        </div>
      </section>
      )}
    </MarketingLayout>
  );
}

/**
 * v2.6.9 batch CK · CityBody — renders the per-city long-form copy with
 * formatting preserved regardless of how the admin input it.
 *
 * - If the stored body looks like HTML (contains a `<` followed by a tag
 *   name) we render it via dangerouslySetInnerHTML so the rich-text editor
 *   output and the seeded default both keep their <h2>/<p>/<ul> structure.
 * - If the stored body is plain text (admin pasted raw copy into the
 *   editor and browser flattened it on save), we split on blank lines into
 *   paragraphs and rely on `white-space: pre-line` so every newline inside
 *   a paragraph stays visible. This matches the format that the Clinton
 *   default renders out of the box.
 */
function CityBody({ html }) {
  const value = String(html || "").trim();
  if (!value) return null;
  // Quick heuristic: does this look like HTML?
  const looksLikeHtml = /<\s*(p|div|h\d|ul|ol|li|br|strong|em|a|blockquote|figure|table)\b/i.test(value);
  if (looksLikeHtml) {
    return (
      <div
        className="prose prose-invert max-w-none text-zinc-300 leading-relaxed text-base"
        dangerouslySetInnerHTML={{ __html: value }}
        data-testid="tourism-city-body-html"
      />
    );
  }
  // Plain-text path · split on blank-line boundaries; preserve inner line
  // breaks via the pre-line whitespace rule.
  const paragraphs = value.split(/\n{2,}/);
  return (
    <div
      className="prose prose-invert max-w-none text-zinc-300 leading-relaxed text-base"
      data-testid="tourism-city-body-plain"
    >
      {paragraphs.map((p, i) => (
        <p key={`p-${i}-${p.slice(0, 24)}`} className="whitespace-pre-line">{p}</p>
      ))}
    </div>
  );
}

